import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import go_n from '../../Images/go_n.gif';
import go_o from '../../Images/go_o.gif';
import close_n from '../../Images/close_n.gif';
import close_o from '../../Images/close_o.gif';
import popup_bg from '../../Common/Images/ImagesForAlert/popup_bg1.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface StudentEntry {
    SchoolCode: string;
    FamilyCode: string;
    FName: string;
    Std: string;
    LoginTime: string;
}

interface DropdownOption {
    value: string;
    label: string;
}

interface Props {
    onBack?: () => void;
}

const styles = {
    fullScreenWindow: {
        position: 'fixed' as 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'white',
        zIndex: 10000, 
        display: 'flex',
        flexDirection: 'column' as 'column'
    },
    popupHeader: {
        backgroundImage: `url(${popup_bg})`,
        backgroundSize: '100% 100%',
        height: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '10px',
        flexShrink: 0
    },
    popupTitle: {
        color: 'white',
        fontWeight: 'bold' as 'bold',
        fontSize: '11pt',
        fontFamily: 'Arial'
    },
    container: { 
        position: 'relative' as 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as 'column',
        fontFamily: 'Arial, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'white',
        overflow: 'hidden'
    },
    headerSection: { 
        backgroundColor: 'white',
        flexShrink: 0,
        padding: '0px'
    },
    pageTitle: {
        fontSize: '20pt',
        fontWeight: 'bold' as 'bold',
        color: 'Black',
        marginBottom: '10px'
    },
    controlsTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        backgroundColor: '#C1E0FF',
        border: '2px solid #80A0FF'
    },
    controlCell: {
        padding: '8px',
        borderRight: '2px solid #80A0FF',
        verticalAlign: 'top' as 'top'
    },
    label: {
        fontWeight: 'bold' as 'bold',
        display: 'block',
        marginBottom: '3px'
    },
    input: { 
        border: '1px solid #003385',
        padding: '2px',
        fontSize: '12pt',
        fontFamily: 'Calibri',
        width: '100%',
        maxWidth: '140px'
    },
    tableContainer: { 
        flex: 1,
        overflow: 'auto',
        padding: '0px 8px 8px 8px',
        margin: '0 10px',
        maxHeight: '72vh',
        maxWidth:"60%"
    },
    dataTable: { 
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '12pt',
        border: '1px solid black',
    },
    th: { 
        backgroundColor: '#fff',
        color: 'black',
        fontSize: '10pt',
        padding: '8px',
        border: '1px solid #a3c0e8',
        borderBottom: '2px solid gray',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0, 
    },
    td: { 
        padding: '8px',
        borderBottom: '1px solid #000000',
        borderRight: '1px solid #000000',
        fontSize: '10pt',
        backgroundColor: '#E5F2FF',
        textWrap:"nowrap"
    },
    tdAlt: {
        padding: '8px',
        borderBottom: '1px solid #000000',
        borderRight: '1px solid #000000',
        fontSize: '10pt',
        backgroundColor: 'White'
    },
    noRecords: {
        fontSize: '20pt',
        color: 'Black',
        marginTop: '20px',
        paddingLeft: '15px'
    }
};

const ExtentionStudent: React.FC<Props> = ({ onBack }) => {
    const [searchParams] = useSearchParams();
    const calledFrom = searchParams.get('CalledFrom') || '';

    const [countries, setCountries] = useState<DropdownOption[]>([{ value: '--SELECT--', label: '--SELECT--' }]);
    const [selectedCountry, setSelectedCountry] = useState<string>('--SELECT--');
    
    const [schoolCodes, setSchoolCodes] = useState<DropdownOption[]>([{ value: '--SELECT--', label: '--SELECT--' }]);
    const [selectedSchoolCode, setSelectedSchoolCode] = useState<string>('--SELECT--');
    
    const [logins, setLogins] = useState<DropdownOption[]>([{ value: '--SELECT--', label: '--SELECT--' }]);
    const [selectedFamilyCode, setSelectedFamilyCode] = useState<string>('--SELECT--');

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<StudentEntry[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    const [isGoHovered, setIsGoHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);

    useEffect(() => {
        const fetchCountries = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/extension-student/filters/`);
                if (response.ok) {
                    const data = await response.json();
                    const cOpts = data.countries.map((c: string) => ({ value: c, label: c }));
                    setCountries([{ value: '--SELECT--', label: '--SELECT--' }, ...cOpts]);
                }
            } catch (err) { console.error("Error fetching countries", err); }
            finally { setLoading(false); }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchSchoolCodes = async () => {
            if (selectedCountry === '--SELECT--') {
                setSchoolCodes([{ value: '--SELECT--', label: '--SELECT--' }]);
                setSelectedSchoolCode('--SELECT--');
                setLogins([{ value: '--SELECT--', label: '--SELECT--' }]);
                setSelectedFamilyCode('--SELECT--');
                return;
            }
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/extension-student/filters/?country=${encodeURIComponent(selectedCountry)}`);
                if (response.ok) {
                    const data = await response.json();
                    const sOpts = data.schoolCodes.map((s: string) => ({ value: s, label: s }));
                    setSchoolCodes([{ value: '--SELECT--', label: '--SELECT--' }, ...sOpts]);
                    setSelectedSchoolCode('--SELECT--');
                    
                    setLogins([{ value: '--SELECT--', label: '--SELECT--' }]);
                    setSelectedFamilyCode('--SELECT--');
                }
            } catch (err) { console.error("Error fetching school codes", err); }
            finally { setLoading(false); }
        };
        fetchSchoolCodes();
    }, [selectedCountry]);

    useEffect(() => {
        const fetchLogins = async () => {
            if (selectedSchoolCode === '--SELECT--') {
                setLogins([{ value: '--SELECT--', label: '--SELECT--' }]);
                setSelectedFamilyCode('--SELECT--');
                return;
            }
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/extension-student/filters/?schoolCode=${encodeURIComponent(selectedSchoolCode)}`);
                if (response.ok) {
                    const data = await response.json();
                    const lOpts = data.logins.map((l: string) => ({ value: l, label: l }));
                    setLogins([{ value: '--SELECT--', label: '--SELECT--' }, ...lOpts]);
                    setSelectedFamilyCode('--SELECT--');
                }
            } catch (err) { console.error("Error fetching logins", err); }
            finally { setLoading(false); }
        };
        fetchLogins();
    }, [selectedSchoolCode]);

    const handleGoClick = async () => {
        setLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            const params = new URLSearchParams({
                country: selectedCountry,
                schoolCode: selectedSchoolCode,
                familyCode: selectedFamilyCode,
            });

            const response = await fetch(`${API_BASE_URL}api/extension-student/generate/?${params.toString()}`);
            if (!response.ok) throw new Error("Failed to load report data.");
            
            const data = await response.json();
            setReportData(data.data || []);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.fullScreenWindow}>
            
            <div style={styles.container}>
                <div style={styles.headerSection}>
                    <div style={styles.pageTitle}>Extension Student List</div>

                    <table style={styles.controlsTable}>
                        <tbody>
                            <tr>
                                <td style={{...styles.controlCell, borderRight: 'none', width: '100px'}}>
                                    <span style={styles.label}>Country:</span>
                                    <select style={styles.input} value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}>
                                        {countries.map((c, i) => <option key={i} value={c.value}>{c.label}</option>)}
                                    </select>
                                </td>
                                <td style={{...styles.controlCell, borderRight: 'none', width: '100px'}}>
                                    <span style={styles.label}>SchoolCode:</span>
                                    <select style={styles.input} value={selectedSchoolCode} onChange={e => setSelectedSchoolCode(e.target.value)}>
                                        {schoolCodes.map((d, i) => <option key={i} value={d.value}>{d.label}</option>)}
                                    </select>
                                </td>
                                <td style={{...styles.controlCell, borderRight: 'none', width: '100px'}}>
                                    <span style={styles.label}>FamilyCode:</span>
                                    <select style={styles.input} value={selectedFamilyCode} onChange={e => setSelectedFamilyCode(e.target.value)}>
                                        {logins.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
                                    </select>
                                </td>
                                <td style={{ ...styles.controlCell, textAlign: 'center', verticalAlign: 'middle', width: '80px', borderLeft: '2px solid #80A0FF' }}>
                                    <img 
                                        src={isGoHovered ? go_o : go_n}
                                        alt="Go" 
                                        onClick={handleGoClick}
                                        onMouseEnter={() => setIsGoHovered(true)}
                                        onMouseLeave={() => setIsGoHovered(false)}
                                        style={{ cursor: 'pointer', width: '80px' }}
                                    />
                                </td>
                                <td style={{ ...styles.controlCell, textAlign: 'center', verticalAlign: 'middle', borderRight: 'none', width: '80px', borderLeft: '2px solid #80A0FF' }}>
                                    <img 
                                        src={isCloseHovered ? close_o : close_n}
                                        alt="Close" 
                                        onClick={() => {
                                            if (onBack) onBack();
                                            else if (calledFrom) window.close();
                                            else window.history.back();
                                        }}
                                        onMouseEnter={() => setIsCloseHovered(true)}
                                        onMouseLeave={() => setIsCloseHovered(false)}
                                        style={{ cursor: 'pointer', width: '80px' }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {loading && <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center'}}><Loading /></div>}
                {error && <div style={{ color: 'red', padding: '10px' }}>{error}</div>}

                {hasSearched && reportData.length === 0 && !loading && (
                    <div style={styles.noRecords}>No Extension Student</div>
                )}

                <div style={styles.tableContainer}>
                    {reportData.length > 0 && (
                        <table style={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Sl No</th>
                                    <th style={styles.th}>Dealer Code</th>
                                    <th style={styles.th}>Login</th>
                                    <th style={styles.th}>Student Name</th>
                                    <th style={styles.th}>Study Year</th>
                                    <th style={styles.th}>Logged Date Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((row, idx) => {
                                    const rowStyle = idx % 2 === 0 ? styles.td : styles.tdAlt;
                                    return (
                                        <tr key={idx}>
                                            <td style={rowStyle}>{idx + 1}</td>
                                            <td style={rowStyle}>{row.SchoolCode}</td>
                                            <td style={rowStyle}>{row.FamilyCode}</td>
                                            <td style={rowStyle}>{row.FName}</td>
                                            <td style={rowStyle}>{row.Std}</td>
                                            <td style={rowStyle}>{row.LoginTime || ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {alertInfo.show && (
                    <CustomAlert
                        message={alertInfo.message}
                        onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                    />
                )}
            </div>
        </div>
    );
};

export default ExtentionStudent;